import React, { useMemo, useState } from "react";

import { ROW_HOVER } from "./constants";
import {
  AuditSourceTitle,
  PanelHeader,
  ReportSelector,
  ReportTypeIcon,
  RowChevron,
  StatusCounts,
  TwoColumn,
} from "./shared";
import { formatTemplatePath } from "./utils";

export const SuggestionsList = ({
  groups,
  loading,
  actionable,
  project,
  reports,
  reportFilter,
  onReportFilter,
  onOpen,
}) => {
  const [severityFilter, setSeverityFilter] = useState("fail");
  const sorted = useMemo(() => {
    return groups
      .map((group) => ({
        ...group,
        items: severityFilter === "fail"
          ? group.items.filter((item) => item.severity === "fail")
          : group.items,
      }))
      .filter((group) => group.items.length > 0)
      .sort((a, b) => {
        const aFail = a.items.filter((i) => i.severity === "fail").length;
        const aWarn = a.items.length - aFail;
        const bFail = b.items.filter((i) => i.severity === "fail").length;
        const bWarn = b.items.length - bFail;
        // pages × (2×fails + warns)
        return (b.items.length * (2 * bFail + bWarn)) - (a.items.length * (2 * aFail + aWarn));
      });
  }, [groups, severityFilter]);

  return (
    <TwoColumn
      main={(
        <>
          <PanelHeader
            title={(
              <AuditSourceTitle
                noun="Suggestions"
                project={project}
                reports={reports}
                reportFilter={reportFilter}
              />
            )}
            count={sorted.length}
            filters={(
              <>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="rounded-full border border-theme-secondary bg-theme-main px-2.5 py-1 text-xs font-medium text-theme-text"
                >
                  <option value="fail">Fails only</option>
                  <option value="all">Fails & warnings</option>
                </select>
                <ReportSelector
                  reports={reports}
                  value={reportFilter || ""}
                  onChange={onReportFilter}
                />
                {!actionable && (
                  <span className="text-xs text-amber-500">Read-only</span>
                )}
              </>
            )}
          />
          {loading && !sorted.length ? (
            <p className="text-sm text-theme-secondary-text">Loading suggestions…</p>
          ) : (
            <ul className="space-y-0.5 rounded-xl border border-theme-secondary p-1">
              {sorted.map((group) => {
                let fail = 0;
                let warn = 0;
                group.items.forEach((item) => {
                  if (item.severity === "fail") fail += 1;
                  else warn += 1;
                });
                const preview = [...group.items]
                  .sort((a, b) => (a.severity === "fail" ? 0 : 1) - (b.severity === "fail" ? 0 : 1))
                  .slice(0, 3)
                  .map((item) => formatTemplatePath(item.site_page_path) || item.site_page_path)
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <li key={group.key}>
                    <button
                      type="button"
                      onClick={() => onOpen(group)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left ${ROW_HOVER}`}
                    >
                      <ReportTypeIcon type={group.report_type} className="h-6 w-6 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{group.title}</p>
                        {preview && (
                          <p className="truncate text-xs text-theme-secondary-text">{preview}</p>
                        )}
                      </div>
                      <StatusCounts
                        pages={group.items.length}
                        counts={{ fail, warn }}
                        keys={["fail", "warn"]}
                      />
                      <RowChevron />
                    </button>
                  </li>
                );
              })}
              {!sorted.length && (
                <li className="px-4 py-8 text-center text-sm text-theme-secondary-text">
                  {severityFilter === "fail"
                    ? "No fail suggestions. Switch filter to include warnings."
                    : "No suggestions right now."}
                </li>
              )}
            </ul>
          )}
        </>
      )}
    />
  );
};
